import * as React from 'react';
import {
  RadioGroup as RACRadioGroup,
  Radio as RACRadio,
  type RadioGroupProps as RACRadioGroupProps,
} from 'react-aria-components';
import { CheckLine } from '@mingcute/react';

import { cn } from '../lib/cn';

/**
 * Klaret RadioCards (handoff §10.3 — card-style single-select).
 * Bigger touch targets + title/description. Used for campaign type, cadence
 * presets, DSAR type, etc. Built on RAC RadioGroup/Radio.
 * Selected card: accent border + --tvl-selected bg + inner accent radio dot.
 * Focus = global 2px ink outline — no accent ring.
 */
export interface RadioCardOption {
  value: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface RadioCardsProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  options: RadioCardOption[];
  /** Grid columns at >=sm. Default: 1. */
  columns?: 1 | 2 | 3;
  name?: string;
  className?: string;
  'aria-label'?: string;
}

const colClass: Record<NonNullable<RadioCardsProps['columns']>, string> = {
  1: 'sm:grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
};

export const RadioCards = React.forwardRef<HTMLDivElement, RadioCardsProps>(
  (
    { value, defaultValue, onValueChange, options, columns = 1, name, className, ...aria },
    ref,
  ) => (
    <RACRadioGroup
      ref={ref}
      value={value}
      defaultValue={defaultValue}
      onChange={onValueChange}
      name={name}
      className={cn('grid grid-cols-1 gap-2', colClass[columns], className)}
      {...(aria['aria-label'] ? { 'aria-label': aria['aria-label'] } : {})}
    >
      {options.map((opt) => (
        <RACRadio
          key={opt.value}
          value={opt.value}
          isDisabled={opt.disabled}
          className={cn(
            'group relative flex cursor-pointer items-start gap-3 rounded-[var(--tvl-r3)] border p-3 text-left',
            'border-[var(--tvl-hairline)] bg-[var(--tvl-raised)]',
            'transition-colors duration-[var(--tvl-d-fast)]',
            'hover:border-[var(--tvl-border-input)]',
            'data-[selected]:border-[var(--tvl-accent)] data-[selected]:bg-[var(--tvl-selected)]',
            'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
          )}
        >
          {({ isSelected, isDisabled }) => (
            <>
              {opt.icon ? (
                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center text-[var(--tvl-ink-2)]">
                  {opt.icon}
                </span>
              ) : null}
              <span className="flex min-w-0 flex-1 flex-col">
                <span
                  className={cn(
                    'text-sm font-medium text-[var(--tvl-ink)]',
                    isSelected && 'font-[500]',
                    isDisabled && 'text-[var(--tvl-ink-disabled)]',
                  )}
                >
                  {opt.label}
                </span>
                {opt.description ? (
                  <span
                    className={cn(
                      'mt-0.5 text-xs text-[var(--tvl-ink-3)]',
                      isDisabled && 'text-[var(--tvl-ink-disabled)]',
                    )}
                  >
                    {opt.description}
                  </span>
                ) : null}
              </span>
              {/* trailing radio indicator — accent dot when unselected, check when selected */}
              <span
                aria-hidden
                className={cn(
                  'mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                  'transition-colors duration-[var(--tvl-d-fast)]',
                  isSelected
                    ? 'border-[var(--tvl-accent)] bg-[var(--tvl-accent)]'
                    : 'border-[var(--tvl-border-input)] bg-[var(--tvl-raised)]',
                )}
              >
                {isSelected ? (
                  <CheckLine size={10} color="var(--tvl-on-accent)" aria-hidden />
                ) : null}
              </span>
            </>
          )}
        </RACRadio>
      ))}
    </RACRadioGroup>
  ),
);
RadioCards.displayName = 'RadioCards';
